import PageHero from '../components/PageHero.jsx';
import KeyFacts from '../components/KeyFacts.jsx';
import SafetyNotice from '../components/SafetyNotice.jsx';
import ResourceList from '../components/ResourceList.jsx';
import { FadeUp } from '../components/Motion.jsx';
import { IconVial, IconSparkle } from '../components/Icons.jsx';

const EMA_URL = 'https://www.ema.europa.eu/en/medicines/human/EPAR/foscan#product-information';

const keyFacts = [
  { label: 'Affected areas', value: 'Oral cavity, pharynx, nasal cavity and larynx' },
  { label: 'Most common type', value: 'Squamous cell carcinoma' },
  { label: 'Tumour location', value: 'Over 90% in the mucosa of the oral cavity, tongue, pharynx and larynx' },
  { label: 'Treatment options', value: 'Surgery, radiotherapy, chemotherapy, photodynamic therapy' },
  { label: 'PDT drug\u2013light interval', value: '4 days' },
];

const related = [
  { label: 'Photodynamic therapy', meta: 'How the treatment works', to: '/pdt', Icon: IconSparkle },
  { label: 'Product information (EMA)', meta: 'European Medicines Agency', href: EMA_URL, Icon: IconVial },
];

const riskFactors = [
  { title: 'Age', desc: 'Men over 40 years of age have a particularly high incidence.' },
  { title: 'Sunlight', desc: 'A contributing factor for cancers of the lip.' },
  { title: 'Alcohol', desc: 'Alcohol abuse is a known contributing factor.' },
  { title: 'Tobacco', desc: 'Smoking or other tobacco use.' },
  { title: 'HPV', desc: 'Human Papilloma viruses, with increasing importance.' },
];

const symptoms = [
  'Persistent pain or numbness in the throat',
  'Pain or difficulty with swallowing',
  'Persistent hoarseness or a change in voice quality',
  'Bleeding in the mouth or throat',
  'Sores or lesions in the oral cavity',
  'Enlargement of a lymph gland in the neck, which is often painless',
];

const treatments = [
  {
    title: 'Surgery',
    desc: 'Aims to completely remove the cancer, and may include removal of affected lymph nodes or bone, if necessary. Reconstructive or plastic surgery may also be required.',
  },
  {
    title: 'Radiotherapy',
    desc: 'The use of high-energy rays to destroy cancer cells. May be used with or without surgery to destroy areas of cancer that cannot be removed surgically.',
  },
  {
    title: 'Chemotherapy',
    desc: 'The use of anticancer drugs to eliminate cancer cells. May be used in conjunction with surgery or radiotherapy to try to increase their effectiveness.',
  },
  {
    title: 'Photodynamic therapy',
    desc: 'A light-activated therapy using the latest drug and laser technology, recently made available for patients with advanced head and neck cancer.',
  },
];

export default function HeadNeckCancer() {
  return (
    <>
      <PageHero
        eyebrow="INDICATION • ONCOLOGY"
        title="Head &amp; neck cancer"
        subtitle="Malignant tumours of the oral cavity, pharynx, nasal cavity and larynx."
      />

      <div className="page-shell">
        <div className="page-grid">
          <div className="page-main">
            <FadeUp>
              <section className="page-section" id="what-is">
                <h2 className="page-section-title">What is head and neck cancer?</h2>
                <p className="page-body">
                  <strong>Head and neck cancer</strong> is the term given to a variety of
                  malignant tumours that develop in the oral cavity (mouth), the pharynx
                  (throat), the nasal cavity and the larynx.
                </p>
                <p className="page-body">
                  Most head and neck cancers are squamous cell carcinomas; however, other
                  tumour types may also be seen. More than 90% of head and neck tumours
                  develop in the mucosa of the oral cavity, the tongue, the pharynx and
                  the larynx.
                </p>
                <p className="page-body">
                  In 2008, more than 600,000 new cases were reported worldwide, of which
                  350,000 were fatal. The past ten years showed an incline in the
                  appearance of head and neck tumours, especially in women.
                </p>
              </section>
            </FadeUp>

            <FadeUp>
              <section className="page-section" id="risk-factors">
                <h2 className="page-section-title">Contributing risk factors</h2>
                <p className="page-body">
                  Factors known to contribute to the risk of developing head and neck
                  cancer include:
                </p>
                <ul className="fact-list">
                  {riskFactors.map((r) => (
                    <li key={r.title} className="fact-list-item">
                      <span className="fact-list-term">{r.title}</span>
                      <span className="fact-list-desc">{r.desc}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </FadeUp>

            <FadeUp>
              <section className="page-section" id="symptoms">
                <h2 className="page-section-title">Signs and symptoms</h2>
                <p className="page-body">
                  The most common symptoms of head and neck cancer include:
                </p>
                <ul className="bullet-list">
                  {symptoms.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
                <p className="page-body">
                  Sores or lesions in the oral cavity can be signs of a developing oral
                  cancer. The first important sign of head and neck cancer may be
                  enlargement of a lymph gland in the neck, which is often painless.
                </p>
              </section>
            </FadeUp>

            <SafetyNotice title="Early detection matters">
              <p>
                Cancers of the head and neck are curable if caught early enough.
                Therefore, it is very important that a doctor is consulted.
              </p>
            </SafetyNotice>

            <FadeUp>
              <section className="page-section" id="treatment">
                <h2 className="page-section-title">Treatment options</h2>
                <p className="page-body">
                  The type of therapy used to treat head and neck cancer depends on the
                  location, size and type of tumour.
                </p>
                <div className="option-grid">
                  {treatments.map((t) => (
                    <div key={t.title} className="option-card">
                      <h3 className="option-card-title">{t.title}</h3>
                      <p className="option-card-desc">{t.desc}</p>
                    </div>
                  ))}
                </div>
              </section>
            </FadeUp>

            <FadeUp>
              <section className="page-section" id="pdt">
                <h2 className="page-section-title">Photodynamic therapy for advanced disease</h2>
                <p className="page-body">
                  Patients receive a light-sensitive drug by injection. Then, 4 days later,
                  the tumour is illuminated with a laser light to activate the drug. The aim
                  of photodynamic therapy is to obtain local clearance of the tumour, and
                  thus reduce any symptoms associated with the cancer.
                </p>
              </section>
            </FadeUp>
          </div>

          <div className="page-aside">
            <KeyFacts items={keyFacts} />
            <ResourceList title="Related information" items={related} />
          </div>
        </div>
      </div>
    </>
  );
}